import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { signOutUser } from '../firebase/authService';
import { getPrayerUnitName } from '../data/prayerUnits';
import { getGideonPrayerPoints, getGideonEvents } from '../data/gideonSeed';
import Footer from '../components/Footer';

const APK_URL = 'https://expo.dev/artifacts/eas/5xguG1EpDELXbKzQw8eseM.apk';

const Dashboard = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOutUser();
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const prayerPoints = getGideonPrayerPoints();
  const events = getGideonEvents();
  const currentPrayerPoint = prayerPoints[0];
  const isGideonMember = userData?.isGideonMember === true;

  const displayName = userData?.firstName
    ? `${userData.firstName} ${userData.lastName || ''}`.trim()
    : currentUser?.displayName || currentUser?.email || 'Member';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-prayer-red via-red-700 to-prayer-red-dark">
        <div className="container-max px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">
              {t('dashboard.title', 'Dashboard')}
            </h1>
            <button
              onClick={handleSignOut}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-colors"
            >
              {t('dashboard.signOut', 'Sign Out')}
            </button>
          </div>
        </div>
      </div>

      <main className="container-max px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Welcome / Profile Summary */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t('dashboard.welcome', 'Welcome')}, {displayName}
          </h2>
          <p className="text-gray-600">
            {t('dashboard.welcomeSubtitle', 'Continue your prayer journey with the movement.')}
          </p>
        </section>

        {/* Current Prayer Unit */}
        {userData?.prayerUnit && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('dashboard.currentPrayerUnit', 'Your Prayer Unit')}
            </h2>
            <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
              <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {userData.prayerUnitName || getPrayerUnitName(userData.prayerUnit) || userData.prayerUnit}
                </p>
              </div>
            </div>
          </section>
        )}

        {/* Gideon Status Card */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {t('dashboard.gideonStatus', 'Gideon Force')}
          </h2>
          <div className={`rounded-xl border p-6 ${isGideonMember ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
            {isGideonMember ? (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-amber-900">
                    {t('dashboard.gideonMember', 'You are a Gideon Force member')}
                  </p>
                  <p className="text-sm text-amber-800 mt-1">
                    {t('dashboard.gideonMemberSubtitle', 'Stay connected with prayer points and events.')}
                  </p>
                  <button
                    onClick={() => navigate('/gideon-force')}
                    className="mt-3 text-sm font-medium text-amber-700 hover:text-amber-900 underline"
                  >
                    {t('dashboard.viewGideonPage', 'View Gideon Force page')} →
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {t('dashboard.gideonNotMember', 'Join the Gideon Force')}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {t('dashboard.gideonNotMemberSubtitle', 'The vanguard unit of the Prayer Movement. Enlist to access prayer points and events.')}
                  </p>
                  <button
                    onClick={() => navigate('/gideon-force#enlist')}
                    className="mt-3 inline-flex items-center px-4 py-2 bg-prayer-red text-white rounded-lg font-medium hover:bg-prayer-red-light transition-colors"
                  >
                    {t('dashboard.enlistGideon', 'Enlist Now')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Gideon Prayer Points */}
        {currentPrayerPoint && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('dashboard.gideonPrayerPoints', 'Current Gideon Prayer Focus')}
            </h2>
            <div className="rounded-xl border border-gray-200 p-6 bg-gray-50">
              <p className="text-sm font-medium text-prayer-red mb-2">
                {currentPrayerPoint.weekLabel} · {currentPrayerPoint.focus}
              </p>
              <p className="text-sm text-gray-600 italic mb-4">
                {currentPrayerPoint.focalScripture}
              </p>
              <ul className="space-y-2">
                {currentPrayerPoint.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-prayer-red mt-0.5">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/gideon-force#prayer-points')}
                className="mt-4 text-sm font-medium text-prayer-red hover:text-prayer-red-light"
              >
                {t('dashboard.viewAllPrayerPoints', 'View all prayer points')} →
              </button>
            </div>
          </section>
        )}

        {/* Gideon Events */}
        {events.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('dashboard.gideonEvents', 'Gideon Events')}
            </h2>
            <div className="space-y-3">
              {events.slice(0, 4).map((event, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 transition-colors"
                >
                  <div className="w-10 h-10 bg-prayer-red/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-prayer-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{event.title}</p>
                    <p className="text-sm text-gray-500">{event.schedule}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => navigate('/gideon-force#events')}
              className="mt-4 text-sm font-medium text-prayer-red hover:text-prayer-red-light"
            >
              {t('dashboard.viewAllEvents', 'View all events')} →
            </button>
          </section>
        )}

        {/* APK / Mobile App Access */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {t('dashboard.mobileApp', 'Mobile App')}
          </h2>
          <p className="text-gray-600 mb-4">
            {t('dashboard.mobileAppSubtitle', 'Download the app and sign in with the same account.')}
          </p>
          <a
            href={APK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-prayer-red text-white rounded-xl font-medium hover:bg-prayer-red-light transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t('dashboard.downloadApk', 'Download Beta APK')}
          </a>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
